import { useEffect, useRef, useState } from "react";

export type MaterialTier = "opaque" | "css" | "refractive";

interface WebGlResources {
  program: WebGLProgram;
  resolutionLocation: WebGLUniformLocation | null;
}

export const LIQUID_GLASS_DEFAULTS = Object.freeze({
  blurAmount: 7,
  refraction: 1.18,
  chromaticAberration: 0.16,
  edgeHighlight: 0.22,
  specular: 0.18,
  fresnel: 1.1,
  cornerRadius: 58,
  zRadius: 46,
  brightness: 0.04,
  saturation: 0.18,
  distortion: 0.18,
  opacity: 0.96,
  tintStrength: 0.06,
  shadowOpacity: 0.34,
  shadowSpread: 18,
  shadowOffsetY: 4,
  floating: false,
  button: false,
  bevelMode: 0,
});

const vertexShader = `#version 300 es
in vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}`;

const fragmentShader = `#version 300 es
precision highp float;
uniform vec2 resolution;
out vec4 color;
void main() {
  vec2 uv = gl_FragCoord.xy / resolution;
  float edge = smoothstep(.12, .0, abs(uv.y - .07)) + smoothstep(.12, .0, abs(uv.y - .93));
  // Keep the ambient optical field in the same cool-white palette as the
  // atlas. A magenta fringe reads as a separate backdrop on light screens,
  // while the reference globe uses only white and ice blue.
  vec3 spectrum = vec3(.07, .11, .22) * edge;
  color = vec4(spectrum, .028 * edge);
}`;

function createShader(
  gl: WebGL2RenderingContext,
  type: number,
  source: string,
): WebGLShader | null {
  const shader = gl.createShader(type);
  if (shader === null) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  return gl.getShaderParameter(shader, gl.COMPILE_STATUS) === true
    ? shader
    : null;
}

function createProgram(gl: WebGL2RenderingContext): WebGlResources | null {
  const vertex = createShader(gl, gl.VERTEX_SHADER, vertexShader);
  const fragment = createShader(gl, gl.FRAGMENT_SHADER, fragmentShader);
  if (vertex === null || fragment === null) return null;
  const program = gl.createProgram();
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  if (gl.getProgramParameter(program, gl.LINK_STATUS) !== true) return null;
  return {
    program,
    resolutionLocation: gl.getUniformLocation(program, "resolution"),
  };
}

function requestedTier(): MaterialTier {
  const override = new URLSearchParams(window.location.search).get("material");
  if (override === "opaque" || override === "css" || override === "refractive")
    return override;
  const reducedTransparency = window.matchMedia(
    "(prefers-reduced-transparency: reduce)",
  ).matches;
  const saveData =
    (navigator as Navigator & { connection?: { saveData?: boolean } })
      .connection?.saveData === true;
  return reducedTransparency || saveData ? "opaque" : "refractive";
}

export function LiquidGlassField({
  onTierChange,
}: {
  onTierChange: (tier: MaterialTier) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tier, setTier] = useState<MaterialTier>("opaque");

  useEffect(() => {
    const preference = requestedTier();
    if (preference !== "refractive") {
      setTier(preference);
      onTierChange(preference);
      return;
    }

    const canvas = canvasRef.current;
    if (canvas === null) {
      setTier("css");
      onTierChange("css");
      return;
    }
    const gl = canvas.getContext("webgl2", { alpha: true, antialias: false });
    if (gl === null) {
      setTier("css");
      onTierChange("css");
      return;
    }

    const resources = createProgram(gl);
    if (resources === null) {
      setTier("css");
      onTierChange("css");
      return;
    }

    const positionLocation = gl.getAttribLocation(
      resources.program,
      "position",
    );
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );
    gl.useProgram(resources.program);
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    let frozen = false;
    let frame = 0;

    const render = () => {
      frame = 0;
      if (frozen) return;
      const ratio = Math.min(window.devicePixelRatio, 2);
      const width = Math.round(window.innerWidth * ratio);
      const height = Math.round(window.innerHeight * ratio);
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      gl.viewport(0, 0, width, height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform2f(resources.resolutionLocation, width, height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    };

    const requestRender = () => {
      if (frame === 0) frame = window.requestAnimationFrame(render);
    };
    const handleMotion = (event: Event) => {
      frozen = (event as CustomEvent<{ moving: boolean }>).detail.moving;
      if (!frozen) requestRender();
    };
    const handleContextLoss = (event: Event) => {
      event.preventDefault();
      setTier("css");
      onTierChange("css");
    };

    setTier("refractive");
    onTierChange("refractive");
    render();
    window.addEventListener("resize", requestRender);
    window.addEventListener("ps:map-motion", handleMotion);
    canvas.addEventListener("webglcontextlost", handleContextLoss);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", requestRender);
      window.removeEventListener("ps:map-motion", handleMotion);
      canvas.removeEventListener("webglcontextlost", handleContextLoss);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(resources.program);
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };
  }, [onTierChange]);

  return (
    <canvas
      ref={canvasRef}
      className="liquid-field"
      data-active={tier === "refractive"}
      data-chromatic-aberration={LIQUID_GLASS_DEFAULTS.chromaticAberration}
      data-refraction={LIQUID_GLASS_DEFAULTS.refraction}
      aria-hidden="true"
    />
  );
}
